// eslint disable react/no-array-index-key
import React from 'react';
import PropTypes from 'prop-types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';
import currencyFormat from 'utils/currencyFormat';
import GraphNoData from '../../assets/images/graphNoData.png';
import { PositiveDotRenderer, NegativeDotRenderer } from './DotRenderer';
import './CommissionLineChart.scss';
import CustomTooltip from './CustomToolTip';

function CommissionLineChart({ commissionDateWise }) {
  const COLORS = {
    positive: '#009D19',
    negative: '#f87171'
  };

  const interpolateZero = (p1, p2, key) => {
    const ratio = p1[key] / (p1[key] - p2[key]);
    const interpolatedX = p1.x + ratio * (p2.x - p1.x);
    return {
      x: interpolatedX,
      date: p2?.date,
      [key]: 0
    };
  };

  const dotRenderers = {
    positive: PositiveDotRenderer,
    negative: NegativeDotRenderer
  };

  const splitBySign = (data, key) => {
    const segments = [];
    let currentSegment = [];

    for (let i = 0; i < data?.length; i++) {
      const curr = data[i];
      const prev = i > 0 ? data[i - 1] : null;

      const currSign = curr[key] >= 0;
      const prevSign = prev?.[key] >= 0;

      if (i === 0 || currSign === prevSign) {
        currentSegment.push(curr);
      } else {
        const zeroPoint = interpolateZero(prev, curr, key);
        currentSegment.push(zeroPoint);
        segments.push({
          data: currentSegment,
          sign: prev[key] >= 0 ? 'positive' : 'negative'
        });
        currentSegment = [zeroPoint, curr];
      }
    }

    if (currentSegment.length) {
      segments.push({
        data: currentSegment,
        sign: currentSegment[0][key] >= 0 ? 'positive' : 'negative'
      });
    }

    return segments;
  };

  const segments = splitBySign(commissionDateWise, 'total');

  return (
    <ResponsiveContainer width="100%" height={400}>
      {commissionDateWise?.length > 0 ? (
        <LineChart data={commissionDateWise} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="category"
            fontSize="13px"
            interval="preserveStartEnd"
            minTickGap={20}
            label={{
              value: 'Date Range',
              position: 'insideBottom',
              offset: -20,
              fontSize: 13,
              fill: 'black'
            }}
            stroke="black"
            axisLine={{ stroke: 'black' }}
            tick={{ fill: 'black' }}
            tickLine={{ stroke: 'black' }}
            tickFormatter={(x) => commissionDateWise[x]?.date || ''}
            allowDuplicatedCategory={false}
          />
          <YAxis
            label={{
              value: 'Commission (₹)',
              angle: -90,
              position: 'insideLeft',
              offset: -13,
              fontSize: 13,
              fill: 'black'
            }}
            stroke="black"
            axisLine={{ stroke: 'black' }}
            tickLine={{ stroke: 'black' }}
            width={70}
            tick={{ fill: 'black' }}
            tickFormatter={(value) => currencyFormat(value, true, false)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Tooltip />
          <Legend
            content={
              <ul className="custom-legend">
                <li style={{ marginRight: 20, display: 'flex', alignItems: 'center' }}>
                  <div className="legend-dot positive" />
                  <span>Commission Earned</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'center' }}>
                  <div className="legend-dot negative" />
                  <span>Negative Commission</span>
                </li>
              </ul>
            }
          />
          <ReferenceLine y={0} stroke="black" strokeWidth={2} strokeDasharray="5 5" />
          {segments.map((seg) => {
            const key = `${seg.sign}-${seg.data[0].x}-${seg.data[seg.data.length - 1].x}`;
            const Dot = dotRenderers[seg.sign];
            return (
              <Line
                key={key}
                data={seg.data}
                type="linear"
                dataKey="total"
                dot={<Dot />}
                stroke={COLORS[seg.sign]}
              />
            );
          })}
        </LineChart>
      ) : (
        <div className="graph-no-data">
          <img src={GraphNoData} width="320px" height="170px" alt="Seems like no data" />
        </div>
      )}
    </ResponsiveContainer>
  );
}
CommissionLineChart.propTypes = {
  commissionDateWise: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      x: PropTypes.number.isRequired
    })
  ).isRequired
};

export default CommissionLineChart;
